import React, { useState, useEffect } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { motion } from 'framer-motion';
import API_BASE_URL from '../config/api';
import { FiDownload, FiFileText, FiSearch } from 'react-icons/fi';

const Resources = () => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchResources = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/manage_resources.php`);
                const data = await response.json();
                if (data.status === 'success') {
                    setResources(data.data);
                }
            } catch (error) {
                console.error("Error fetching resources:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchResources();
    }, []);

    const filteredResources = resources.filter(res => 
        res.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="bg-slate-50 min-h-screen">
            <Navbar />
            
            {/* Header */}
            <div className="pt-32 pb-20 bg-white relative overflow-hidden">
                {/* Brand Background Elements */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full filter blur-[100px] translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600 rounded-full filter blur-[100px] -translate-x-1/2 translate-y-1/2"></div>
                </div>
                
                <div className="container mx-auto px-4 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-block px-4 py-1 rounded-full bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest mb-4"
                    >
                        Academic Center
                    </motion.div>
                    <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-5xl md:text-6xl font-black text-slate-900 mb-6 uppercase tracking-tight"
                    >
                        LEARNING <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">RESOURCES</span>
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-slate-600 max-w-2xl mx-auto font-medium"
                    >
                        Access our comprehensive collection of PDF study materials, guides, and academic resources.
                    </motion.p>
                </div>
            </div>

            {/* Search and Content */}
            <div className="container mx-auto px-4 py-16">
                <div className="max-w-4xl mx-auto">
                    <div className="relative mb-12">
                        <FiSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
                        <input 
                            type="text" 
                            placeholder="Search resources by title or description..." 
                            className="w-full pl-16 pr-6 py-5 rounded-2xl border-none shadow-xl shadow-slate-200 focus:ring-2 focus:ring-orange-500 outline-none text-lg font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                        </div>
                    ) : filteredResources.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {filteredResources.map((res, index) => (
                                <motion.div
                                    key={res.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:border-orange-200 transition-colors group relative overflow-hidden"
                                >
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/5 rounded-full -mr-12 -mt-12 transition-transform group-hover:scale-150 duration-700"></div>
                                    
                                    <div className="flex items-start gap-4 relative z-10">
                                        <div className="p-4 bg-orange-50 rounded-xl text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-all duration-300">
                                            <FiFileText size={32} />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                                                {res.title}
                                            </h3>
                                            <p className="text-slate-600 mb-6 line-clamp-2 text-sm">
                                                {res.description}
                                            </p>
                                            <a 
                                                href={`${API_BASE_URL}/../${res.file_path}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-bold text-sm uppercase tracking-wider hover:bg-orange-600 transition-all shadow-md shadow-slate-200"
                                            >
                                                <FiDownload /> Download PDF
                                            </a>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <div className="inline-block p-6 bg-slate-100 rounded-full text-slate-400 mb-4">
                                <FiFileText size={48} />
                            </div>
                            <p className="text-slate-500 text-xl font-medium">No resources found matching your search.</p>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Resources;
